import { capitalCase } from 'change-case';
import { Box, Grid, Tab, Tabs } from '@mui/material';
import useTabs from '../../../../hooks/useTabs';
import ContactDetails from '../contact-detail/ContactDetails';
import PatientGroups from './PatientGroups';
import Insurance from '../insurance/Insurance';
import PatientReferralCases from './PatientReferralCases';
import PersonalDetails from '../personal-detail/PersonalDetails';
import { PatientDetails } from 'src/careflow/care-coordination/patient/detail/patientDetails.type';
import { TwoColumnLayout } from 'src/utils/layout';
import { IdentifierDetails } from '../identifier-detail/IdentifierDetails';
import { NewAddressDetails } from '../address-detail/NewAddressDetails';
import PatientDocuments from './PatientDocuments';
import { useLocation, Link } from 'react-router-dom';
import PatientHealth from './PatientHealth';
import { usePatient, useValueSets } from 'src/careflow/ph-core/fhir-client/fhir-react';
import { ValueSetWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/ValueSet';
import { PatientWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import { useCallback, useMemo } from 'react';
import produce from 'immer';
import { Identifier } from 'src/careflow/ph-core/fhir-client/resources';
import PatientTasks from '../PatientTasks';
import ExpandableCard from 'src/components/common/ExpandableCard';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import EngagementHub from 'src/careflow/engagement-hub/patient-engagement/common/engagement-hub-tab/EngagementHubTab';
import { checkAclValidation } from 'src/careflow/ph-core/admin/permissions/util/permission.utils';
import crsAcls from 'src/careflow/ph-core/admin/permissions/util/crs/crsAcls';
import PatientConsents from './PatientConsents';
import useTenantConfigData from 'src/careflow/ph-core/multi-tenant/tenant/hook/useTenantConfigData';
import { upperCase } from 'lodash';
import { useSnackbar } from 'notistack';
import CustomFieldsDetails from 'src/sections/careflow/custom-fields/components/CustomFieldDetails';

export default function PatientTabs({ patient, onRefresh }: PatientDetails) {
  const history = useLocation();
  const { i18n } = useLocales();

  const pathuser =
    history.pathname?.slice(history.pathname?.lastIndexOf('/') + 1, history.pathname?.length)
      .length > 20 ||
    history.pathname?.slice(history.pathname?.lastIndexOf('/') + 1, history.pathname?.length) ==
      'general'
      ? 'general'
      : history.pathname?.slice(history.pathname?.lastIndexOf('/') + 1, history.pathname?.length);

  const PATIENT_TABS = [
    {
      value: `${i18n('patients.details.tabs.general', 'crs')}`,
      component: <PatientDetailsView patient={patient} onRefresh={onRefresh} />,
      path: 'general',
    },
    ...(checkAclValidation({ acls: [crsAcls.CRS.PATIENT.TABS.REFERRAL_CASES.VIEW] })
      ? [
          {
            value: `${i18n('patients.details.tabs.referal', 'crs')}`,
            component: <PatientReferralCases patient={patient} />,
            path: 'referrals-cases',
          },
        ]
      : []),
    ...(checkAclValidation({ acls: [crsAcls.CRS.PATIENT.TABS.HEALTH.VIEW] })
      ? [
          {
            value: `${i18n('patients.details.tabs.health', 'crs')}`,
            component: <PatientHealth patient={patient} />,
            path: 'health',
          },
        ]
      : []),
    ...(checkAclValidation({ acls: [crsAcls.CRS.PATIENT.TABS.GROUPS.VIEW] })
      ? [
          {
            value: `${i18n('patients.details.tabs.groups', 'crs')}`,
            component: <PatientGroups patient={patient} onRefresh={onRefresh} />,
            path: 'groups',
          },
        ]
      : []),
    ...(checkAclValidation({ acls: [crsAcls.CRS.PATIENT.TABS.INSURANCE.VIEW] })
      ? [
          {
            value: `${i18n('patients.details.tabs.insurance', 'crs')}`,
            component: <Insurance patient={patient} i18n={i18n} />,
            path: 'insurance',
          },
        ]
      : []),
    ...(checkAclValidation({ acls: [crsAcls.CRS.PATIENT.TABS.DOCUMENTS.VIEW] })
      ? [
          {
            value: `${i18n('patients.details.tabs.documents', 'crs')}`,
            component: <PatientDocuments patient={patient} />,
            path: 'documents',
          },
        ]
      : []),
    ...(checkAclValidation({ acls: [crsAcls.CRS.PATIENT.TABS.TASKS.VIEW] })
      ? [
          {
            value: `${i18n('patients.details.tabs.tasks', 'crs')}`,
            component: <PatientTasks patient={patient} />,
            path: 'tasks',
          },
        ]
      : []),
    ...(checkAclValidation({ acls: [crsAcls.CRS.PATIENT.TABS.ENGAGEMENT_HUB.VIEW] })
      ? [
          {
            value: `${i18n('patients.details.tabs.engagement', 'crs')}`,
            component: <EngagementHub patient={patient} />,
            path: 'engagement-hub',
          },
        ]
      : []),
    ...(checkAclValidation({ acls: [crsAcls.CRS.PATIENT.TABS.CONSENTS.VIEW] })
      ? [
          {
            value: `${i18n('patients.details.tabs.consents', 'crs')}`,
            component: <PatientConsents patient={patient} />,
            path: 'consents',
          },
        ]
      : []),
  ];

  const { currentTab, onChangeTab } = useTabs(
    PATIENT_TABS.filter((tab) => pathuser == tab.path)[0].path
  );

  return (
    <>
      <Tabs
        allowScrollButtonsMobile
        variant="scrollable"
        scrollButtons="auto"
        value={currentTab}
        onChange={onChangeTab}
      >
        {PATIENT_TABS.map((tab) => (
          <Tab
            component={Link}
            to={`/dashboard/crs/patient/${patient.id}/${tab.path}`}
            disableRipple
            key={tab.path}
            label={capitalCase(tab.value)}
            value={tab.path}
          />
        ))}
      </Tabs>

      {PATIENT_TABS.map((tab) => {
        const isMatched = tab.path === currentTab;
        return isMatched && <Box key={tab.value}>{tab.component}</Box>;
      })}
    </>
  );
}

function PatientDetailsView({ patient, onRefresh }: PatientDetails) {
  const { i18n } = useLocales();
  const { componentsData } = useTenantConfigData();
  const { patient: patientConfig } = componentsData;

  const { enqueueSnackbar } = useSnackbar();

  const valueSetsToRetrieve = ['patient-identifier-types', 'ph-gender-identity'];

  const [valueSets] = useValueSets({
    filter: {
      _id: valueSetsToRetrieve.join(','),
    },
    map: ValueSetWrapper,
  });

  const [patientIdentifierTypesVS, genderIdentityVS] = useMemo(
    () =>
      valueSetsToRetrieve.map((valueSet) => valueSets.find(({ id }) => id === valueSet) ?? null),
    [valueSets]
  );

  const patientIdentifierTypes = patientIdentifierTypesVS?.asList?.() ?? [];

  const [, { update: updatePatient }] = usePatient(patient.id!, { map: PatientWrapper });

  const onUpdateIdentifiers = useCallback(
    async (newIdentifiers: Identifier[]) => {
      await updatePatient(
        produce(patient!, (draft) => {
          draft.identifier = newIdentifiers;
        })
      );
    },
    [patient]
  );

  const handleOnAddressesSave = async (addresses) => {
    await updatePatient(
      produce(patient!, (draft) => {
        draft.address = addresses;
      })
    );
  };

  const handleTelecomSave = async (telecom) => {
    try {
      await updatePatient(
        produce(patient!, (draft) => {
          draft.telecom = [...telecom];
        })
      );
      enqueueSnackbar(`${i18n('patients.title', 'crs')} was updated.`);
    } catch (error) {
      enqueueSnackbar(`${i18n('patients.title', 'crs')} was not updated.`, { variant: 'error' });
    }
  };

  const detailsPermissions = useMemo(
    () => ({
      isAllowedToViewPersonal: checkAclValidation({
        acls: [crsAcls.CRS.PATIENT.PERSONAL_DETAILS.VIEW],
      }),
      isAllowedToViewContact: checkAclValidation({
        acls: [crsAcls.CRS.PATIENT.CONTACT_DETAILS.VIEW],
      }),
      isAllowedToViewCustomFields: checkAclValidation({
        acls: [crsAcls.CRS.PATIENT.CUSTOM_FIELDS.VIEW],
      }),
    }),
    []
  );

  const visibleSections = [
    detailsPermissions?.isAllowedToViewPersonal,
    detailsPermissions?.isAllowedToViewContact,
    detailsPermissions?.isAllowedToViewCustomFields,
  ].filter(Boolean).length;

  const layout = visibleSections >= 3 ? 4 : 6;

  return (
    <TwoColumnLayout>
      {detailsPermissions?.isAllowedToViewPersonal ? (
        <Grid item xs={layout}>
          <PersonalDetails patient={patient} genderIdentityVS={genderIdentityVS} fullWidth={true} />
        </Grid>
      ) : (
        <></>
      )}
      {detailsPermissions?.isAllowedToViewContact ? (
        <Grid item xs={layout}>
          <ContactDetails
            patient={patient}
            telecom={patient?.telecom}
            update={handleTelecomSave}
            title={upperCase(i18n('patients.details.contactDetails.title', 'crs'))}
            fullWidth={true}
            permissions={{
              isAllowedToAdd: true,
              isAllowedToEdit: true,
              isAllowedToDelete: true,
            }}
            valueSetsIds={['patient-contact-use-email', 'patient-contact-use-phone']}
          />
        </Grid>
      ) : (
        <></>
      )}

      {detailsPermissions?.isAllowedToViewCustomFields ? (
        <Grid item xs={layout}>
          <CustomFieldsDetails patient={patient} />
        </Grid>
      ) : (
        <></>
      )}

      {checkAclValidation({ acls: [crsAcls.CRS.PATIENT.ADDRESS_DETAILS.VIEW] }) ? (
        <div style={{ width: '100%', paddingLeft: '10px', paddingTop: '10px' }}>
          <ExpandableCard
            title={i18n('patients.details.addressDetails.title', 'crs')}
            sxProp={{ m: 1 }}
          >
            <NewAddressDetails
              addressess={patient?.address ?? []}
              onAddressesSave={handleOnAddressesSave}
              permissions={{
                isAllowedToAdd: checkAclValidation({
                  acls: [crsAcls.CRS.PATIENT.ADDRESS_DETAILS.ADD],
                }),
                isAllowedToEdit: checkAclValidation({
                  acls: [crsAcls.CRS.PATIENT.ADDRESS_DETAILS.EDIT],
                }),
                isAllowedToDelete: checkAclValidation({
                  acls: [crsAcls.CRS.PATIENT.ADDRESS_DETAILS.DELETE],
                }),
                isAllowedAddressHousingStatus: checkAclValidation({
                  acls: [crsAcls.CRS.PATIENT.ADDRESS_DETAILS.ADD_HOUSING_STATUS],
                }),
              }}
              config={patientConfig}
            />
          </ExpandableCard>
        </div>
      ) : (
        <></>
      )}

      {checkAclValidation({ acls: [crsAcls.CRS.PATIENT.IDENTIFIER_DETAILS.VIEW] }) ? (
        <div style={{ width: '100%', paddingLeft: '10px', paddingTop: '10px' }}>
          <ExpandableCard
            title={i18n('patients.details.identifierDetails.title', 'crs')}
            sxProp={{ m: 1 }}
          >
            <IdentifierDetails
              identifiers={patient?.identifier ?? []}
              catalog={patientIdentifierTypes}
              systemVS={patientIdentifierTypesVS?.getConceptSystem()}
              onUpdateIdentifiers={onUpdateIdentifiers}
              permissions={{
                isAllowedToAdd: checkAclValidation({
                  acls: [crsAcls.CRS.PATIENT.IDENTIFIER_DETAILS.ADD],
                }),
                isAllowedToEdit: checkAclValidation({
                  acls: [crsAcls.CRS.PATIENT.IDENTIFIER_DETAILS.EDIT],
                }),
                isAllowedToDelete: checkAclValidation({
                  acls: [crsAcls.CRS.PATIENT.IDENTIFIER_DETAILS.DELETE],
                }),
                isAllowedToViewHidden: checkAclValidation({
                  acls: [crsAcls.CRS.PATIENT.IDENTIFIER_DETAILS.HIDDEN.VIEW],
                }),
                isAllowedToEditHidden: checkAclValidation({
                  acls: [crsAcls.CRS.PATIENT.IDENTIFIER_DETAILS.HIDDEN.EDIT],
                }),
                isAllowedToDeleteHidden: checkAclValidation({
                  acls: [crsAcls.CRS.PATIENT.IDENTIFIER_DETAILS.HIDDEN.DELETE],
                }),
                isAllowedToEditExternal: checkAclValidation({
                  acls: [(crsAcls.CRS.PATIENT.IDENTIFIER_DETAILS.EXTERNAL as any)?.EDIT],
                }),
                isAllowedToDeleteExternal: checkAclValidation({
                  acls: [(crsAcls.CRS.PATIENT.IDENTIFIER_DETAILS.EXTERNAL as any)?.DELETE],
                }),
              }}
            />
          </ExpandableCard>
        </div>
      ) : (
        <></>
      )}

      {/* <Section>
        <OtherDetails />
      </Section> */}
    </TwoColumnLayout>
  );
}
